import classNames from "classnames";
import { useAppLayoutStore, useAppLayoutStoreActions } from "PFApp/stores/app_layout_store";
import useIsAuditPlannerPermitted from "PFApp/use_is_audit_planner_permitted";
import useIsChatEnabled from "PFApp/use_is_chat_enabled";
import { useIsMarketplacePermitted } from "PFApp/use_is_marketplace_permitted";
import useIsWorkflowPermitted from "PFApp/use_is_workflow_permitted";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { FeatureFlag, PermissionRule } from "PFTypes";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import useBookingModulePermissions from "../../use_booking_module_permissions";
import { AdminNavItem } from "./items/admin_nav_item";
import { AnalyticsNavItem } from "./items/analytics_nav_item";
import { AuditPlannerNavItem } from "./items/audit_planner_nav_item";
import { BookingNavItem } from "./items/booking_nav_item";
import { ChatNavItem } from "./items/chat_nav_item";
import { CustomLinksNavItem } from "./items/custom_links_nav_item";
import { FeedNavItem } from "./items/feed_nav_item";
import { HelpNavItem } from "./items/help_nav_item";
import { MarketplaceNavItem } from "./items/marketplace_nav_item";
import { NavLogo } from "./items/nav_logo";
import { NewActivitiesNavItem } from "./items/new_activities_nav_item";
import { NotificationsNavItem } from "./items/notifications_nav_item";
import { ProfilesNavItem } from "./items/profile_nav_item";
import { ProPilotNavItem } from "./items/propilot_nav_item";
import { SearchNavItem } from "./items/search_nav_item";
import { WorkflowNavItem } from "./items/workflow_nav_item";
import { YouNavItem } from "./items/you_nav_item";
import css from "./navbar.module.scss";
import { MenuTrigger } from "./parts/menu_trigger";
import { CommonNavItemProps } from "./parts/nav_item";

export const Navbar = () => {
  const location = useLocation();
  const [openKey, setOpenKey] = useState(null);

  const isNavBarRevealedOnMobile = useAppLayoutStore((state) => state.isNavBarRevealedOnMobile);
  const isBackdropVisible = useAppLayoutStore((state) => state.isBackdropVisible);
  const { hideBackdrop, hideNavBar, showBackdrop, showNavBar } = useAppLayoutStoreActions();

  const { data: currentAccount } = useCurrentAccount();
  const { data: currentProfile } = useCurrentProfile();

  const closeSubMenu = () => {
    hideBackdrop();
    hideNavBar();
    setOpenKey(null);
  };

  useEffect(() => {
    // It closes submenu when navigating to another page
    // Link component cannot have both href and onClick - it's causing issues with navigation and RouterPrompt
    // Issues related:
    // https://github.com/remix-run/react-router/issues/12182
    // https://github.com/remix-run/react-router/issues/11430
    if (openKey) {
      closeSubMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state?.redirectId]);

  useEffect(() => {
    // It closes submenu when clicking on backdrop itselft
    if (!isBackdropVisible && openKey) {
      setOpenKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBackdropVisible]);

  const openSubMenu = (key) => {
    showBackdrop();
    setOpenKey(key);
  };

  const commonProps: CommonNavItemProps = {
    openKey,
    openSubMenu,
    closeSubMenu
  };

  const isEnabled = useIsFeatureEnabled();
  const isPermittedTo = useIsPermittedTo(currentProfile);

  const isAccountSwitched = currentProfile.accountId && currentProfile.accountId !== currentAccount.id;

  const isChatEnabled = useIsChatEnabled();
  const isWorkflowPermitted = useIsWorkflowPermitted();
  const isAnalyticsPermitted =
    isEnabled(FeatureFlag.AnalyticsModule) &&
    (isPermittedTo(PermissionRule.Reporter) ||
      isPermittedTo(PermissionRule.UsageAnalytics) ||
      (isEnabled(FeatureFlag.PowerBI) && isPermittedTo(PermissionRule.DynamicInsights)));
  const isProfilesPermitted = isPermittedTo(PermissionRule.ProfileList);
  const isMarketplacePermitted = useIsMarketplacePermitted();
  const isAuditPlannerPermitted = useIsAuditPlannerPermitted();
  const { isEnabled: isBMEnabled, isPermittedToAccess } = useBookingModulePermissions();
  const isActivityFeedPermitted = isEnabled(FeatureFlag.ActivityFeed);

  return (
    <nav data-qa-id="NavBar" role="navigation">
      <div
        className={classNames(css.root, {
          [css.revealed]: isNavBarRevealedOnMobile,
          [css.elevated]: !!openKey
        })}
      >
        <ul className={css.list}>
          <NavLogo />
          {!isAccountSwitched && (
            <>
              <SearchNavItem {...commonProps} />
              <NewActivitiesNavItem {...commonProps} />

              <li className={css.sep} />

              {isMarketplacePermitted && <MarketplaceNavItem {...commonProps} />}
              {isWorkflowPermitted && <WorkflowNavItem {...commonProps} />}
              {isAuditPlannerPermitted && <AuditPlannerNavItem {...commonProps} />}
              {isAnalyticsPermitted && <AnalyticsNavItem {...commonProps} />}
              {isActivityFeedPermitted && <FeedNavItem {...commonProps} />}
              {isProfilesPermitted && <ProfilesNavItem {...commonProps} />}
              {isBMEnabled && isPermittedToAccess && <BookingNavItem {...commonProps} />}
              <li style={{ flexGrow: 2 }} />
              <li className={css.sep} />

              <NotificationsNavItem {...commonProps} />
              <HelpNavItem {...commonProps} />
              {isChatEnabled && <ChatNavItem {...commonProps} />}
              <CustomLinksNavItem {...commonProps} />
            </>
          )}
          <AdminNavItem {...commonProps} />
          <YouNavItem {...commonProps} />
          <ProPilotNavItem {...commonProps} />
        </ul>
      </div>
      <MenuTrigger
        disabled={isNavBarRevealedOnMobile}
        onClick={() => {
          showNavBar();
          showBackdrop();
        }}
      />
    </nav>
  );
};
